import React from 'react';

const shimmer = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
};

// Add keyframes to document once
if (typeof document !== 'undefined' && !document.getElementById('skeleton-style')) {
  const style = document.createElement('style');
  style.id = 'skeleton-style';
  style.textContent = '@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }';
  document.head.appendChild(style);
}

export const SkeletonBox = ({ width = '100%', height = 16, borderRadius = 8, style = {} }) => (
  <div style={{ width, height, borderRadius, ...shimmer, flexShrink: 0, ...style }} />
);

export const SkeletonText = ({ lines = 3, lastWidth = '60%' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBox key={i} height={14} borderRadius={6} width={i === lines - 1 ? lastWidth : '100%'} />
    ))}
  </div>
);

export const SkeletonCard = ({ imageHeight = 200 }) => (
  <div style={{ background: 'white', borderRadius: 20, overflow: 'hidden', border: '1px solid var(--border-light)' }}>
    <SkeletonBox height={imageHeight} borderRadius={0} />
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SkeletonBox height={18} width="60%" borderRadius={6} />
      <SkeletonText lines={2} lastWidth="40%" />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <SkeletonBox height={32} width="50%" borderRadius={8} />
        <SkeletonBox height={32} width="40%" borderRadius={8} />
      </div>
    </div>
  </div>
);

export const SkeletonProductCard = () => (
  <div style={{ background: 'white', borderRadius: 20, overflow: 'hidden', border: '1px solid var(--border-light)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
    <SkeletonBox height={220} borderRadius={0} />
    <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SkeletonBox height={16} width="55%" borderRadius={6} />
      <SkeletonBox height={12} width="80%" borderRadius={5} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        <SkeletonBox height={12} width="30%" borderRadius={5} />
        <SkeletonBox height={30} width="35%" borderRadius={8} />
      </div>
    </div>
  </div>
);

export const SkeletonConfigurator = () => (
  <div style={{ background: 'white', borderRadius: 24, border: '1px solid var(--border)', overflow: 'hidden' }}>
    <div style={{ padding: '20px 28px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SkeletonBox height={10} width={100} borderRadius={5} />
        <SkeletonBox height={16} width={180} borderRadius={6} />
      </div>
      <SkeletonBox height={36} width={80} borderRadius={10} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
      <div style={{ padding: '24px 20px', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SkeletonBox height={10} width={60} borderRadius={5} />
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonBox key={i} height={38} borderRadius={8} />
        ))}
      </div>
      <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SkeletonBox height={10} width={80} borderRadius={5} />
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBox key={i} height={38} borderRadius={8} />
        ))}
      </div>
    </div>
    <div style={{ padding: '20px 28px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SkeletonBox height={48} borderRadius={12} />
      <SkeletonBox height={36} borderRadius={10} />
    </div>
  </div>
);

export const SkeletonTestimonial = () => (
  <div style={{ flexShrink: 0, width: 300, borderRadius: 20, overflow: 'hidden', background: 'white', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
    <SkeletonBox height={200} borderRadius={0} />
    <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SkeletonBox height={12} width="70%" borderRadius={5} />
      <SkeletonText lines={3} lastWidth="50%" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8 }}>
        <SkeletonBox width={32} height={32} borderRadius="50%" />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SkeletonBox height={12} width="60%" borderRadius={5} />
          <SkeletonBox height={10} width="80%" borderRadius={5} />
        </div>
      </div>
    </div>
  </div>
);

export default SkeletonBox;
